import { useLocation } from "react-router-dom";
import { ALUMNI_ROOT, CLIENT_QUEUE, CONTACT_LOG, DASHBOARD } from "@/lib/adminPilot";

/** The `admin.alumni.nav.*` key naming each opener, as the sidebar labels it. */
type OpenerLabel = "directory" | "queue" | "log" | "dashboard";

/**
 * The console pages that open an alum's profile, each with the sidebar label (`admin.alumni.nav.*`)
 * that names it. The profile's back link returns to whichever one opened it, as the browser's Back
 * does, instead of always to the directory. An allowlist, so the link can only ever lead to one of
 * these — adding an entry point is one line here.
 */
const OPENERS: ReadonlyMap<string, OpenerLabel> = new Map<string, OpenerLabel>([
    [
        ALUMNI_ROOT,
        "directory",
    ],
    [
        CLIENT_QUEUE,
        "queue",
    ],
    [
        CONTACT_LOG,
        "log",
    ],
    [
        DASHBOARD,
        "dashboard",
    ],
]);

/** Router state on a link into a profile: the page that opened it, with its query (the dashboard's month). */
export interface OpenedFrom {
    from: string;
}

/** `state` when it names one of the `OPENERS`; anything else is dropped. */
export const openedFrom = (state: unknown): OpenedFrom | undefined => {
    const from = (state as Partial<OpenedFrom> | null)?.from;
    if (typeof from !== "string") return undefined;
    return OPENERS.has(from.split("?")[0] ?? "") ? { from } : undefined;
};

/** Where a profile's back link goes and the nav label it shows. Opened directly, the directory. */
export const backTarget = (opened: OpenedFrom | undefined): { to: string; labelKey: OpenerLabel } => {
    const to = opened?.from ?? ALUMNI_ROOT;
    return { to, labelKey: OPENERS.get(to.split("?")[0] ?? "") ?? "directory" };
};

/** For an entry point's link into a profile: the page it is on now. */
export const useOpenedFromHere = (): OpenedFrom => {
    const { pathname, search } = useLocation();
    return { from: `${pathname}${search}` };
};

/** Inside a profile: who opened it, to pass along on links between the profile and its full record. */
export const useOpenedFrom = (): OpenedFrom | undefined => openedFrom(useLocation().state);
